import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import type { FindRelatedResult } from "../../lib/types";

/**
 * Sprint 26.2 — the cross-tag mention graph for one subject.
 *
 * Wraps `find_related`, which returns the four relationship layers (explicit
 * references, tree context, reverse mentions, forward mentions) for a library
 * node (`ln_…`) or an entity row. Tasks are neither, so `find_related` answers
 * `not_found` for them — we swallow that into an empty result so the shared
 * {@link RelatedSection} can lean on `get_back_references` instead without an
 * error boundary.
 */

const EMPTY = (id: string): FindRelatedResult => ({ id, name: "", type: "", relationships: {} });

export function useFindRelated(id: string | undefined, enabled = true) {
  return useQuery<FindRelatedResult>({
    queryKey: ["find-related", id],
    queryFn: async () => {
      const r = await mcpClient.toolsCall<FindRelatedResult>("find_related", { id });
      // not_found (e.g. a task id) and any other tool error degrade to empty —
      // the Related section is additive chrome, never a hard failure.
      if (r.isError) return EMPTY(id as string);
      return (r.structuredContent as FindRelatedResult) ?? EMPTY(id as string);
    },
    enabled: enabled && !!id,
    staleTime: 10_000,
  });
}
